import { enqueueSnackbar } from "notistack";
import { useContext, useRef, useState } from "react";
import { useDebounce } from "rooks";

import { CreateLabelValueData, useLabels } from "api/services/useLabels";
import { useReviewPageService } from "api/services/useReviewPage";
import { TIME_TO_UNDO_LABEL } from "config/constants";
import { UNSURE_LABEL } from "config/LabelMapping";
import { ServiceWorkerContext, ServiceWorkerContextType } from "context/serviceWorkerContext";
import { useAppDispatch } from "hooks/store";
import { setUnsavedAnnotations } from "store/slices/FocusModal/focusModalSlice";
import { ListResponse } from "types/api";
import { CanvasAnnotation } from "types/imageAnnotations";
import { DisplayBBoxInfo, ImageQuery, Posicheck, REVIEW_REASON_CHOICES } from "types/imageQueries";
import { LabelValue } from "types/labelValue";
import { User, UserType } from "types/users";
import { PresentedInfoToLabel, checkIsGroundlightBoundingBoxes, contructViewWhenLabeling } from "utils/ReviewPageUtils";

export interface LabelImageQueryProps {
  currentImageQuery: Posicheck;
  label: string;
  annotations?: CanvasAnnotation[];
  feedbackText?: string;
  reviewBBoxInfo?: DisplayBBoxInfo;
}

export interface UseCreateLabelProps {
  imageAppearTime: React.MutableRefObject<number>;
  isGroundTruth: boolean;
  postEvent: (event: string, data: any) => Promise<void>;
  sendLabelTimerRef: React.MutableRefObject<number | NodeJS.Timeout | undefined>;
  shouldStartPooling: boolean;
  startPooling: () => void;
  updateState: (labeledImageQuery: CreateLabelValueData, shouldRemoveRecentIQ: boolean) => void;
  user: User;
  noExamplesData?: ListResponse<ImageQuery> | undefined;
  yesExamplesData?: ListResponse<ImageQuery> | undefined;
  handlePostSubmit?: () => void;
}

export const useCreateLabel = (props: UseCreateLabelProps) => {
  const {
    imageAppearTime,
    isGroundTruth,
    postEvent,
    sendLabelTimerRef,
    shouldStartPooling,
    startPooling,
    updateState,
    user,
    noExamplesData,
    yesExamplesData,
    handlePostSubmit,
  } = props;

  const isUserLabeler = user.userprofile.customer_type === UserType.Labeler;
  const isUserLabelerManager = user.userprofile.customer_type === UserType.Manager;
  const isUserSuperuser = user.is_superuser || user.userprofile.customer_type === UserType.Groundlight;

  const isUserCustomer = !isUserLabelerManager && !isUserLabeler && !isUserSuperuser;

  const shouldStartPoolingRef = useRef(shouldStartPooling);
  shouldStartPoolingRef.current = shouldStartPooling;

  const [isLoading, setIsLoading] = useState(false);
  const { isUpdateReady, updateServiceWorker } = useContext(ServiceWorkerContext) as ServiceWorkerContextType;
  const { reviewPageSummary, getReviewPageSummary } = useReviewPageService(!user.only_labeler_ui && isUserCustomer);

  const { createLabelAsync } = useLabels();
  const dispatch = useAppDispatch();

  const enqueueQuery = async (
    labeledImageQuery: CreateLabelValueData,
    presentedInfo: PresentedInfoToLabel,
    isDisagreementCheck: boolean
  ) => {
    const newSeconds = !isDisagreementCheck ? 0 : TIME_TO_UNDO_LABEL;

    return new Promise<LabelValue | null>((resolve, reject) => {
      sendLabelTimerRef.current = setTimeout(async () => {
        let labelCreated = null;

        setIsLoading(true);
        try {
          labelCreated = await createLabelAsync(labeledImageQuery);
          if (labeledImageQuery.rois && labeledImageQuery.rois.length > 0) {
            dispatch(setUnsavedAnnotations(false));
          }
          if (handlePostSubmit) {
            handlePostSubmit();
          }

          await postEvent("", { event_type: "review_page_snapshot", description: JSON.stringify(presentedInfo) });

          if (shouldStartPoolingRef.current) startPooling();

          if (isUpdateReady && user.only_labeler_ui) {
            updateServiceWorker();
            return;
          }
          if (user.enable_review_options && isUserCustomer) await getReviewPageSummary();
        } catch (error) {
          console.error(error);
          enqueueSnackbar("Error Labeling Image", { variant: "error" });
          reject(error);
        } finally {
          setIsLoading(false);
        }

        resolve(labelCreated);
      }, newSeconds);
    });
  };

  // Object to send to backend when submitting an image
  const constructLabeledImageQuery = ({
    imageQuery,
    label,
    elapsedScreenTime,
    annotations,
    feedbackText,
    isBoundingBoxes,
  }: {
    imageQuery: Posicheck;
    label: string;
    isBoundingBoxes: boolean;
    elapsedScreenTime: number;
    annotations?: CanvasAnnotation[];
    feedbackText?: string;
  }): CreateLabelValueData => ({
    posicheck_id: imageQuery.id,
    label,
    feedback_text: feedbackText,
    rois: isBoundingBoxes ? annotations : null,
    elapsed_screen_time_ms: elapsedScreenTime,
    review_reason: isGroundTruth
      ? REVIEW_REASON_CHOICES.CUSTOMER_INITIATED
      : imageQuery.review_reason || REVIEW_REASON_CHOICES.ESCALATION,

    /**
     * Frontend must send back to the backend the displayed bounding boxes from
     * the posicheck `review_bbox_info` field.
     */
    review_bbox_info: imageQuery.review_bbox_info,
  });

  const getElapsedScreenTime = () => {
    const now = new Date();
    return now.getTime() - imageAppearTime.current;
  };

  const labelImageQuery = async (
    props: LabelImageQueryProps,
    isDisagreementCheck: boolean
  ): Promise<LabelValue | null | undefined> => {
    const { currentImageQuery, label, annotations, feedbackText } = props;
    if (!currentImageQuery) return undefined;

    const isBoundingBoxes = checkIsGroundlightBoundingBoxes(currentImageQuery.predictor);
    const elapsedScreenTime = getElapsedScreenTime();

    const labeledImageQuery = constructLabeledImageQuery({
      imageQuery: currentImageQuery,
      label,
      isBoundingBoxes,
      elapsedScreenTime,
      annotations: annotations || [],
      feedbackText,
    });
    if (!labeledImageQuery.feedback_text) delete labeledImageQuery.feedback_text;

    const presentedInfo: PresentedInfoToLabel = contructViewWhenLabeling(
      currentImageQuery,
      yesExamplesData?.results || [],
      noExamplesData?.results || []
    );
    updateState(labeledImageQuery, isDisagreementCheck);
    const labelCreated = await enqueueQuery(labeledImageQuery, presentedInfo, isDisagreementCheck);

    return labelCreated;
  };

  const debouncedLabelImageQuery = useDebounce(labelImageQuery, 500, {
    leading: true,
    trailing: false,
  }) as typeof labelImageQuery;

  const ignoreHandler = (imageQuery: Posicheck | undefined) => {
    if (imageQuery?.id) {
      createLabelAsync({
        posicheck_id: imageQuery.id,
        label: UNSURE_LABEL,
        review_reason: imageQuery.review_reason || REVIEW_REASON_CHOICES.ESCALATION,
      });
      startPooling();
    }
  };

  return {
    reviewPageSummary,
    labelImageQuery: async (props: LabelImageQueryProps, isDisagreementCheck: boolean) => {
      if (isDisagreementCheck) {
        return debouncedLabelImageQuery(props, true);
      }
      return labelImageQuery(props, false);
    },
    ignoreHandler,
    isLoading,
  };
};
